(() => {
  const TIME_ZONE = 'Europe/London';
  const DEFAULT_CUTOFF_HOUR = 14;
  const UPDATE_INTERVAL = 60 * 1000;
  const DEFAULT_DELIVERY_CONFIG = {
    countryCode: 'GB',
    countryName: 'United Kingdom',
    status: 'domestic',
    transitDaysMin: 1,
    transitDaysMax: 2,
    deliversOnSaturday: true,
    benefitText: 'Free delivery',
    tooltipText: 'Tracked UK delivery after dispatch. Order by 2pm on working days and we will usually dispatch the same day; weekends and bank holidays may add a little extra time.',
    valueLimitEuro: null,
    dispatchPause: null
  };

  const BANK_HOLIDAYS = [
    '2026-01-01',
    '2026-04-03',
    '2026-04-06',
    '2026-05-04',
    '2026-05-25',
    '2026-08-31',
    '2026-12-25',
    '2026-12-28',
    '2027-01-01',
    '2027-03-26',
    '2027-03-29',
    '2027-05-03',
    '2027-05-31',
    '2027-08-30',
    '2027-12-27',
    '2027-12-28',
    '2028-01-03',
    '2028-04-14',
    '2028-04-17',
    '2028-05-01',
    '2028-05-29',
    '2028-08-28',
    '2028-12-25',
    '2028-12-26',
    '2029-01-01',
    '2029-03-30',
    '2029-04-02',
    '2029-05-07',
    '2029-05-28',
    '2029-08-27',
    '2029-12-25',
    '2029-12-26',
    '2030-01-01',
    '2030-04-19',
    '2030-04-22',
    '2030-05-06',
    '2030-05-27',
    '2030-08-26',
    '2030-12-25',
    '2030-12-26'
  ];

  const bankHolidaySet = new Set(BANK_HOLIDAYS);
  let cutoffHour = DEFAULT_CUTOFF_HOUR;
  let cutoffMinutes = cutoffHour * 60;

  function normalizeDeliveryConfig(config) {
    const normalized = Object.assign({}, DEFAULT_DELIVERY_CONFIG, config || {});
    const supportedStatuses = new Set(['domestic', 'pddp', 'pddp_limited', 'unsupported']);

    normalized.countryCode = String(normalized.countryCode || 'GB').toUpperCase();
    normalized.countryName = String(normalized.countryName || normalized.countryCode);
    normalized.status = supportedStatuses.has(normalized.status) ? normalized.status : 'unsupported';
    normalized.transitDaysMin = Math.max(0, Number(normalized.transitDaysMin) || 0);
    normalized.transitDaysMax = Math.max(normalized.transitDaysMin, Number(normalized.transitDaysMax) || 0);
    normalized.deliversOnSaturday = Boolean(normalized.deliversOnSaturday);
    normalized.benefitText = String(normalized.benefitText || '');
    normalized.tooltipText = String(normalized.tooltipText || '');

    const pause = normalized.dispatchPause || {};
    const validDate = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || '')) && dateToIso(isoToDate(value)) === value;
    const pauseCutoffHour = Number(pause.cutoffHour);
    normalized.dispatchPause = Object.assign({}, pause, {
      enabled: pause.enabled === true && validDate(pause.cutoffDate) && validDate(pause.resumeDate) &&
        pause.resumeDate > pause.cutoffDate && Number.isFinite(pauseCutoffHour) && pauseCutoffHour >= 0 && pauseCutoffHour <= 23,
      cutoffHour: pauseCutoffHour
    });

    return normalized;
  }

  function readDeliveryConfig() {
    const globalConfig = window.MioDeliveryPolicy || window.MioDeliveryConfig;

    if (globalConfig && typeof globalConfig === 'object' && !globalConfig.nodeType) {
      return normalizeDeliveryConfig(globalConfig);
    }

    const configElement = typeof document.getElementById === 'function'
      ? document.getElementById('MioDeliveryConfig')
      : null;

    if (!configElement) return normalizeDeliveryConfig();

    try {
      return normalizeDeliveryConfig(JSON.parse(configElement.textContent));
    } catch (error) {
      return normalizeDeliveryConfig();
    }
  }

  function readProductDispatchConfig() {
    if (typeof document.querySelector !== 'function') return;

    const configElement = document.querySelector('script[id^="DispatchConfig-"]');
    if (!configElement) return;

    try {
      const productConfig = JSON.parse(configElement.textContent);
      const configuredCutoff = Number(productConfig.cutoffHour);

      if (Number.isFinite(configuredCutoff) && configuredCutoff >= 0 && configuredCutoff <= 23) {
        cutoffHour = configuredCutoff;
        cutoffMinutes = cutoffHour * 60;
      }

      String(productConfig.ukPublicHolidays || '')
        .split(/[\n,]+/)
        .map((value) => value.trim())
        .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))
        .forEach((value) => bankHolidaySet.add(value));
    } catch (error) {
      /* The shared defaults remain authoritative if section JSON is invalid. */
    }
  }

  const deliveryConfig = readDeliveryConfig();
  window.MioDeliveryPolicy = deliveryConfig;

  const londonFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  const readableDateFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
  const deliveryWindowDateFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'UTC',
    weekday: 'short',
    day: 'numeric',
    month: 'short'
  });
  let initQueued = false;
  let dynamicObserverBound = false;

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function toLondonParts(date = new Date()) {
    const parts = londonFormatter.formatToParts(date);
    const partMap = {};

    parts.forEach((part) => {
      if (part.type !== 'literal') {
        partMap[part.type] = part.value;
      }
    });

    return {
      year: Number(partMap.year),
      month: Number(partMap.month),
      day: Number(partMap.day),
      weekday: partMap.weekday,
      hour: Number(partMap.hour),
      minute: Number(partMap.minute),
      second: Number(partMap.second),
      isoDate: `${partMap.year}-${partMap.month}-${partMap.day}`
    };
  }

  function isoToDate(isoDate) {
    const [year, month, day] = String(isoDate).split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  }

  function dateToIso(date) {
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
  }

  function normalizeToStoreDate(date = new Date()) {
    if (typeof date === 'string') {
      return isoToDate(date);
    }

    return isoToDate(toLondonParts(date).isoDate);
  }

  function addDays(date, days) {
    const nextDate = new Date(date.getTime());
    nextDate.setUTCDate(nextDate.getUTCDate() + days);
    return nextDate;
  }

  function isBankHoliday(date) {
    return bankHolidaySet.has(dateToIso(normalizeToStoreDate(date)));
  }

  function isSunday(date) {
    return normalizeToStoreDate(date).getUTCDay() === 0;
  }

  function isSaturday(date) {
    return normalizeToStoreDate(date).getUTCDay() === 6;
  }

  function canCollect(date) {
    const storeDate = normalizeToStoreDate(date);
    const day = storeDate.getUTCDay();

    return day >= 1 && day <= 5 && !isBankHoliday(storeDate);
  }

  function canDeliver(date, config = deliveryConfig) {
    const storeDate = normalizeToStoreDate(date);
    const day = storeDate.getUTCDay();

    if (isBankHoliday(storeDate) || isSunday(storeDate)) return false;

    return config.deliversOnSaturday || day !== 6;
  }

  function getNextValidDate(fromDate, validator, includeStart = false) {
    let candidate = normalizeToStoreDate(fromDate);

    if (!includeStart) {
      candidate = addDays(candidate, 1);
    }

    while (!validator(candidate)) {
      candidate = addDays(candidate, 1);
    }

    return candidate;
  }

  function getCollectionDate(orderDate = new Date()) {
    const londonParts = toLondonParts(orderDate);
    const orderStoreDate = isoToDate(londonParts.isoDate);
    const currentMinutes = (londonParts.hour * 60) + londonParts.minute;

    const dispatchCutoff = getDispatchCutoffMinutes(orderStoreDate);
    const normalCollectionDate = canCollect(orderStoreDate) && currentMinutes < dispatchCutoff
      ? orderStoreDate
      : getNextValidDate(orderStoreDate, canCollect, false);
    const pause = deliveryConfig.dispatchPause;
    if (!pause.enabled) return normalCollectionDate;

    // The configured restart is an explicit dispatch day, including Saturday.
    // Delivery still follows the existing weekend and bank-holiday calendar.
    const resumeDate = isoToDate(pause.resumeDate);
    const resumeCollectionDate = !isBankHoliday(resumeDate) && !isSunday(resumeDate)
      ? resumeDate
      : getNextValidDate(resumeDate, canCollect, true);
    const afterPauseCutoff = londonParts.isoDate > pause.cutoffDate ||
      (londonParts.isoDate === pause.cutoffDate && currentMinutes >= pause.cutoffHour * 60);
    const normalCollectionIso = dateToIso(normalCollectionDate);

    if (londonParts.isoDate < pause.resumeDate && (afterPauseCutoff ||
      normalCollectionIso > pause.cutoffDate || (normalCollectionIso === pause.cutoffDate && pause.cutoffHour === 0))) {
      return resumeCollectionDate;
    }

    if (isSameStoreDate(orderStoreDate, resumeCollectionDate) && currentMinutes < cutoffMinutes) {
      return resumeCollectionDate;
    }

    return normalCollectionDate;
  }

  function getDispatchCutoffMinutes(orderStoreDate) {
    const pause = deliveryConfig.dispatchPause;
    return pause.enabled && dateToIso(orderStoreDate) === pause.cutoffDate ? pause.cutoffHour * 60 : cutoffMinutes;
  }

  function addDeliveryDays(collectionDate, deliveryDays, config = deliveryConfig) {
    let candidate = normalizeToStoreDate(collectionDate);
    let completedDays = 0;

    while (completedDays < deliveryDays) {
      candidate = addDays(candidate, 1);

      if (canDeliver(candidate, config)) {
        completedDays += 1;
      }
    }

    return candidate;
  }

  function getEstimatedDeliveryDate(orderDate = new Date(), config = deliveryConfig) {
    const collectionDate = getCollectionDate(orderDate);

    return addDeliveryDays(collectionDate, config.transitDaysMin, config);
  }

  function getEstimatedDeliveryWindow(orderDate = new Date(), config = deliveryConfig) {
    const collectionDate = getCollectionDate(orderDate);
    const startDate = addDeliveryDays(collectionDate, config.transitDaysMin, config);
    const endDate = addDeliveryDays(collectionDate, config.transitDaysMax, config);

    return { startDate, endDate };
  }

  function isSameStoreDate(firstDate, secondDate) {
    return dateToIso(normalizeToStoreDate(firstDate)) === dateToIso(normalizeToStoreDate(secondDate));
  }

  function formatDateForCustomer(date) {
    return readableDateFormatter.format(normalizeToStoreDate(date));
  }

  function getOrdinalSuffix(day) {
    const value = Number(day);
    const tens = value % 100;

    if (tens >= 11 && tens <= 13) return 'th';

    switch (value % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  function formatDispatchDateForCustomer(date) {
    const formattedDate = formatDateForCustomer(date);
    const [weekday, day, ...monthParts] = formattedDate.split(' ');

    if (!weekday || !day || !monthParts.length) return formattedDate;

    return `${weekday} (${day}${getOrdinalSuffix(day)} ${monthParts.join(' ')})`;
  }

  function formatWindowDateForCustomer(date) {
    return deliveryWindowDateFormatter.format(normalizeToStoreDate(date)).replace(',', '');
  }

  function getCalendarDayDifference(fromDate, toDate) {
    const startDate = normalizeToStoreDate(fromDate);
    const endDate = normalizeToStoreDate(toDate);
    const millisecondsPerDay = 24 * 60 * 60 * 1000;

    return Math.max(1, Math.round((endDate.getTime() - startDate.getTime()) / millisecondsPerDay));
  }

  function formatDeliveryWindow(orderDate, config = deliveryConfig) {
    const { startDate, endDate } = getEstimatedDeliveryWindow(orderDate, config);

    return `${formatWindowDateForCustomer(startDate)} - ${formatWindowDateForCustomer(endDate)}`;
  }

  function formatRemaining(totalMinutes) {
    const safeMinutes = Math.max(0, totalMinutes);
    const hours = Math.floor(safeMinutes / 60);
    const minutes = safeMinutes % 60;

    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}m`;
  }

  function getDeliveryDetails(orderDate = new Date(), configOverride = deliveryConfig) {
    const config = normalizeDeliveryConfig(configOverride);
    const available = config.status !== 'unsupported';

    if (!available) {
      return {
        available: false,
        status: config.status,
        countryCode: config.countryCode,
        countryName: config.countryName,
        benefitText: '',
        tooltipText: config.tooltipText,
        eyebrowText: `Delivery to ${config.countryName}`,
        cardLabelText: 'Delivery:',
        deliveryWindowText: 'Not currently available',
        estimateText: 'Not currently available',
        countdownText: 'Choose another supported delivery country.'
      };
    }

    const londonParts = toLondonParts(orderDate);
    const orderStoreDate = isoToDate(londonParts.isoDate);
    const collectionDate = getCollectionDate(orderDate);
    const deliveryDate = getEstimatedDeliveryDate(orderDate, config);
    const deliveryWindow = getEstimatedDeliveryWindow(orderDate, config);
    const currentMinutes = (londonParts.hour * 60) + londonParts.minute;
    const minutesUntilCutoff = getDispatchCutoffMinutes(orderStoreDate) - currentMinutes;
    const pause = deliveryConfig.dispatchPause;
    const pauseEstimate = pause.enabled && (londonParts.isoDate > pause.cutoffDate ||
      (londonParts.isoDate === pause.cutoffDate && currentMinutes >= pause.cutoffHour * 60) ||
      (londonParts.isoDate < pause.resumeDate && dateToIso(collectionDate) >= pause.resumeDate));
    const dispatchesToday = !pauseEstimate && isSameStoreDate(orderStoreDate, collectionDate) && minutesUntilCutoff > 0;
    const deliveryWindowText = formatDeliveryWindow(orderDate, config);
    const international = config.status === 'pddp' || config.status === 'pddp_limited';
    const importChargeNote = international
      ? config.status === 'pddp_limited'
        ? ' PDDP eligibility is confirmed at checkout.'
        : ' Import charges prepaid.'
      : '';
    const dispatchDateText = formatDateForCustomer(collectionDate);
    const pauseDispatchText = String(pause.dispatchText || 'Dispatch from __dispatch_date__.').replace('__dispatch_date__', dispatchDateText);
    const countdownText = pauseEstimate
      ? `${pauseDispatchText}${importChargeNote}`
      : dispatchesToday
        ? `Order within ${formatRemaining(minutesUntilCutoff)} cutoff for dispatch today.${importChargeNote}`
        : `Order now for ${formatDispatchDateForCustomer(collectionDate)} dispatch.${importChargeNote}`;
    let tooltipText = config.tooltipText;
    if (pause.enabled) {
      const pauseIntro = pauseEstimate
        ? String(pause.duringTooltip || pauseDispatchText).replace('__dispatch_date__', dispatchDateText)
        : String(pause.beforeTooltip || '');
      const deliveryNote = config.status === 'domestic' ? String(pause.domesticTooltip || '') : config.tooltipText;
      tooltipText = `${pauseIntro} ${deliveryNote}`.trim();
    }

    return {
      available: true,
      status: config.status,
      countryCode: config.countryCode,
      countryName: config.countryName,
      benefitText: config.benefitText,
      tooltipText,
      eyebrowText: international ? `Estimated delivery to ${config.countryName}` : 'Estimated delivery',
      cardLabelText: 'Estimated delivery:',
      collectionDate,
      deliveryDate,
      dispatchesToday,
      pauseEstimate,
      minutesUntilCutoff: Math.max(0, minutesUntilCutoff),
      deliveryDateText: formatDateForCustomer(deliveryDate),
      deliveryWindowText,
      collectionDateText: formatDateForCustomer(collectionDate),
      collectionDispatchDateText: formatDispatchDateForCustomer(collectionDate),
      deliveryWindowStartText: formatDateForCustomer(deliveryWindow.startDate),
      deliveryWindowEndText: formatDateForCustomer(deliveryWindow.endDate),
      estimateText: deliveryWindowText,
      cutoffText: formatRemaining(minutesUntilCutoff),
      countdownText
    };
  }

  function getCartDeliverySubtext(details) {
    if (details.pauseEstimate) {
      return details.countdownText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
    const importChargeNote = details.status === 'pddp'
      ? ' Import charges prepaid.'
      : details.status === 'pddp_limited'
        ? ' PDDP eligibility is confirmed at checkout.'
        : '';

    return `<strong class="mio-cart-delivery-summary__action">Order now</strong> for ${details.collectionDispatchDateText} dispatch.${importChargeNote}`;
  }

  function queryDeliveryTargets() {
    const estimateNodes = Array.from(document.querySelectorAll('[data-mio-delivery-estimate]'));
    const widgetNodes = Array.from(document.querySelectorAll('[data-mio-delivery-widget]'));
    const detailsNodes = Array.from(document.querySelectorAll('[data-mio-delivery-details]'));
    const benefitNodes = Array.from(document.querySelectorAll('[data-mio-delivery-benefit]'));

    return {
      estimateNodes,
      widgetNodes,
      detailsNodes,
      benefitNodes,
      hasTargets: estimateNodes.length > 0 || widgetNodes.length > 0 || detailsNodes.length > 0 || benefitNodes.length > 0
    };
  }

  function updateCardEstimate(element, details) {
    const labelEls = element.querySelectorAll('[data-mio-delivery-label]');
    const dateEls = element.querySelectorAll('[data-mio-delivery-date]');

    if (labelEls.length && dateEls.length) {
      labelEls.forEach((labelEl) => {
        labelEl.textContent = details.cardLabelText;
      });
      dateEls.forEach((dateEl) => {
        dateEl.textContent = details.deliveryWindowText;
      });
    } else {
      element.textContent = `${details.cardLabelText} ${details.deliveryWindowText}`;
    }

    element.classList.toggle('mio-card-delivery-estimate--unsupported', !details.available);
    element.setAttribute('aria-label', details.available ? details.eyebrowText : details.deliveryWindowText);
    element.querySelectorAll('[data-mio-delivery-tooltip]').forEach((tooltip) => {
      tooltip.textContent = details.tooltipText;
    });
  }

  function updateDeliveryBenefit(element, details) {
    if (!details.benefitText) {
      element.hidden = true;
      return;
    }

    let benefitText = details.benefitText;

    if (details.status === 'domestic' && element.classList.contains('mio-product-sticky-atc__shipping-label--desktop')) {
      benefitText = 'Free UK shipping';
    } else if (
      details.status === 'pddp' &&
      element.classList.contains('mio-product-sticky-atc__shipping-label--mobile')
    ) {
      benefitText = 'Import prepaid';
    }

    element.hidden = false;
    element.textContent = benefitText;
    element.setAttribute('aria-label', details.benefitText);
  }

  function updateProductWidget(widget, details) {
    const eyebrowEl = widget.querySelector('.mio-dispatch-card__eyebrow');
    const headlineEl = widget.querySelector('[data-dispatch-headline]');
    const subtextEl = widget.querySelector('[data-dispatch-subtext]');
    const badgeMainEl = widget.querySelector('[data-dispatch-badge-main]');
    const badgeAccentEl = widget.querySelector('[data-dispatch-badge-accent]');

    widget.hidden = false;
    widget.classList.toggle('is-urgent', details.available && details.dispatchesToday && details.minutesUntilCutoff <= 120);

    if (eyebrowEl) {
      eyebrowEl.textContent = details.eyebrowText;
    }

    if (headlineEl) {
      headlineEl.textContent = details.estimateText;
    }

    if (subtextEl) {
      if (!details.available) {
        subtextEl.textContent = details.countdownText;
      } else if (widget.classList.contains('mio-cart-delivery-summary')) {
        subtextEl.innerHTML = getCartDeliverySubtext(details);
      } else {
        subtextEl.textContent = details.countdownText;
      }
    }

    if (badgeMainEl) {
      badgeMainEl.textContent = details.available ? (details.dispatchesToday ? 'Dispatch' : 'Collects') : '';
    }

    if (badgeAccentEl) {
      badgeAccentEl.textContent = details.available
        ? details.dispatchesToday
          ? 'today'
          : details.collectionDateText.split(' ')[0]
        : '';
    }
  }

  function updateDeliveryEstimates(targets = queryDeliveryTargets()) {
    const details = getDeliveryDetails(new Date());

    targets.estimateNodes.forEach((element) => {
      updateCardEstimate(element, details);
    });

    targets.widgetNodes.forEach((widget) => {
      updateProductWidget(widget, details);
    });

    targets.benefitNodes.forEach((element) => {
      updateDeliveryBenefit(element, details);
    });
  }

  function syncDeliveryDetailsState(detailsElement) {
    const summary = detailsElement.querySelector('.mio-card-delivery-estimate__summary');
    const tooltip = detailsElement.querySelector('[data-mio-delivery-tooltip]');
    const isOpen = detailsElement.open;

    if (summary) {
      summary.setAttribute('aria-expanded', String(isOpen));
    }

    if (tooltip) {
      tooltip.setAttribute('aria-hidden', String(!isOpen));
    }
  }

  function closeAllDeliveryDetails(exceptElement = null) {
    document.querySelectorAll('[data-mio-delivery-details][open]').forEach((detailsElement) => {
      if (detailsElement !== exceptElement) {
        detailsElement.open = false;
        syncDeliveryDetailsState(detailsElement);
      }
    });
  }

  function setupDeliveryTooltipInteractions(targets) {
    targets.detailsNodes
      .filter((detailsElement) => !detailsElement.hasAttribute('data-mio-delivery-bound'))
      .forEach((detailsElement) => {
        detailsElement.setAttribute('data-mio-delivery-bound', 'true');
        syncDeliveryDetailsState(detailsElement);

        detailsElement.addEventListener('toggle', () => {
          if (detailsElement.open) {
            closeAllDeliveryDetails(detailsElement);
          }

          syncDeliveryDetailsState(detailsElement);
        });
      });

    if (window.mioDeliveryTooltipGlobalBound) return;

    window.mioDeliveryTooltipGlobalBound = true;

    document.addEventListener('click', (event) => {
      if (!event.target.closest('[data-mio-delivery-details]')) {
        closeAllDeliveryDetails();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeAllDeliveryDetails();
      }
    });
  }

  function initDeliveryEstimates() {
    window.clearInterval(window.mioDeliveryEstimateTimer);
    window.mioDeliveryEstimateTimer = null;
    readProductDispatchConfig();

    const targets = queryDeliveryTargets();

    if (!targets.hasTargets) {
      return;
    }

    setupDeliveryTooltipInteractions(targets);
    updateDeliveryEstimates(targets);

    window.mioDeliveryEstimateTimer = window.setInterval(() => {
      const nextTargets = queryDeliveryTargets();

      if (!nextTargets.hasTargets) {
        window.clearInterval(window.mioDeliveryEstimateTimer);
        window.mioDeliveryEstimateTimer = null;
        return;
      }

      updateDeliveryEstimates(nextTargets);
    }, UPDATE_INTERVAL);
  }

  function queueDeliveryEstimateInit() {
    if (initQueued) return;
    initQueued = true;

    window.requestAnimationFrame(() => {
      initQueued = false;
      initDeliveryEstimates();
    });
  }

  function bindDynamicDeliveryObserver() {
    if (dynamicObserverBound || !document.body || typeof MutationObserver === 'undefined') return;
    dynamicObserverBound = true;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;

          if (
            node.matches('[data-mio-delivery-estimate], [data-mio-delivery-widget], [data-mio-delivery-details], [data-mio-delivery-benefit], product-recommendations') ||
            node.querySelector('[data-mio-delivery-estimate], [data-mio-delivery-widget], [data-mio-delivery-details], [data-mio-delivery-benefit]')
          ) {
            queueDeliveryEstimateInit();
            return;
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  window.MioDeliveryEstimate = {
    bankHolidays: BANK_HOLIDAYS,
    config: deliveryConfig,
    isBankHoliday,
    isSunday,
    canCollect,
    canDeliver,
    addDeliveryDays,
    getCollectionDate,
    getEstimatedDeliveryDate,
    getEstimatedDeliveryWindow,
    formatDeliveryWindow,
    getDeliveryDetails,
    init: initDeliveryEstimates
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initDeliveryEstimates();
      bindDynamicDeliveryObserver();
    });
  } else {
    initDeliveryEstimates();
    bindDynamicDeliveryObserver();
  }

  document.addEventListener('shopify:section:load', initDeliveryEstimates);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      const targets = queryDeliveryTargets();
      if (targets.hasTargets) {
        updateDeliveryEstimates(targets);
      }
    }
  });
})();
